import { useMemo, type JSX } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { fontFamily, textStyle } from "@passfirst/ui";

import { MicrophoneIcon, SparkleIcon } from "../../icons";
import { type Colors, useColors } from "../../theme";

interface Props {
  onSmartPractice: () => void;
  onVoiceCoach: () => void;
}

// Two side-by-side action tiles per spec. Each is a tappable card with
// a coral-soft icon tile + short label + sub. Sub copy is intentionally
// terse ("Targets your weak topics", "Pre-trip, out loud") per spec.
export function ActionTiles({
  onSmartPractice,
  onVoiceCoach,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  return (
    <View style={styles.tiles}>
      <Tile
        styles={styles}
        accent={colors.action}
        label="Smart practice"
        desc="Targets your weak topics"
        icon={<SparkleIcon size={20} color={colors.action} />}
        onPress={onSmartPractice}
      />
      <Tile
        styles={styles}
        accent={colors.action}
        label="Voice coach"
        desc="Pre-trip, out loud"
        icon={<MicrophoneIcon size={20} color={colors.action} />}
        onPress={onVoiceCoach}
      />
    </View>
  );
}

function Tile({
  styles,
  accent,
  label,
  desc,
  icon,
  onPress,
}: {
  styles: ReturnType<typeof makeStyles>;
  accent: string;
  label: string;
  desc: string;
  icon: JSX.Element;
  onPress: () => void;
}): JSX.Element {
  void accent;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={styles.tile}
    >
      <View style={styles.tileIcon}>{icon}</View>
      <Text style={styles.tileName}>{label}</Text>
      <Text style={styles.tileDesc}>{desc}</Text>
    </Pressable>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    tiles: {
      flexDirection: "row",
      gap: 11,
    },
    tile: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.borderSoft,
      borderRadius: 15,
      padding: 16,
      backgroundColor: colors.surface,
    },
    tileIcon: {
      width: 36,
      height: 36,
      borderRadius: 9,
      backgroundColor: colors.surfaceActionSoft,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 12,
    },
    tileName: {
      fontFamily: fontFamily.bold,
      fontSize: 15,
      color: colors.textPrimary,
    },
    tileDesc: {
      ...textStyle.caption,
      color: colors.textMuted,
      marginTop: 3,
    },
  });
}
